<template>
    <BottomModal
        :is-visible="modalStore?.activeModal === MODAL_REVIEW"
        class="review-modal"
        title="Start Peer Review"
        description="Review video content for quality, educational value, scientific integrity, and patient anonymity."
        @close="onClose"
    >
        <form
            novalidate
            class="review-modal__form"
            @submit.prevent="onSubmit"
        >
            <div v-for="(question, index) in questions" :key="index" class="review-modal__question">
                <BaseFormField
                    required
                    :errors="errors['answers'] && typeof answers?.[index] !== 'boolean' ? [errors['answers']] : []"
                >
                    <template #label>
                        <label :for="`q${index}`">{{ question }}</label>
                    </template>

                    <BaseRadios direction="column">
                        <BaseRadio
                            v-for="option in [{ label: 'Yes', value: true }, { label: 'No', value: false }]"
                            :id="`q${index}-${option.value}`"
                            :key="option.value"
                            v-model="answers[index]"
                            :value="option.value"
                            :name="`q${index}`"
                            required
                        >
                            {{ option.label }}
                        </BaseRadio>
                    </BaseRadios>
                </BaseFormField>
            </div>

            <div
                v-if="errorMessage"
                class="review-modal__form-error"
            >
                {{ errorMessage }}
            </div>

            <BaseButton
                :loading="loading"
                class="base-button--wide"
            >
                Submit Review
            </BaseButton>
        </form>
    </BottomModal>
</template>

<script setup>
import SubmitReviewMutation from '~/graphql/mutations/submitReview.graphql';
import { useForm } from 'vee-validate';
import { array, string } from 'yup';
import { useModalStore, MODAL_REVIEW } from '~/store/modal';

const { push } = useTagmanager();

const props = defineProps({
    video: {
        type: Object,
        required: true
    }
});

const modalStore = useModalStore();
const loading = ref(false);
const errorMessage = ref('');

const questions = [
    'Can you confirm that the video does not include any patient-identifying details?',
    'Does the video demonstrate a procedure that aligns with current clinical practice?',
    'In your opinion, is the video and audio quality sufficient for publication on our platform?',
    'Does the content qualify as educational material?',
    'Is the content non-commercial in nature? We encourage the mention of products from the pharmaceutical or device industry, as long as the content remains educational and unbiased.'
];

const validationSchema = array()
    .of(string().required('This question must be answered'))
    .min(questions.length, 'Please answer all questions');

const { errors, handleSubmit, defineField } = useForm({
    validationSchema: {
        answers: validationSchema,
        comments: string().max(500, 'Comments cannot exceed 500 characters')
    },
    initialValues: {
        answers: Array(questions.length).fill(''),
        comments: ''
    }
});

const [answers] = defineField('answers');

const { execute: submitReview } = useAsyncMutation('submit-review');

const onClose = () => {
    push({
        event: 'peer_review_form_closed'
    });
    modalStore.closeModal();
};
const onSubmit = handleSubmit(async(values) => {
    loading.value = true;
    errorMessage.value = '';

    push({ event: 'peer_review_submit_click' });

    const negativeAnswersCount = values.answers.filter(answer => !answer).length;

    try {
        if (
            negativeAnswersCount >= 2 &&
            !confirm('Reviews with 2 or more negative answers will be marked as negative and reviewed by the team. Do you want to proceed?')
        ) {
            return;
        }
        await submitReview({
            mutation: SubmitReviewMutation,
            variables: {
                answers: values.answers,
                video_id: props.video.id // eslint-disable-line
            }
        });

        // todo check if we need to call the peer_review_form_closed event
        modalStore.closeModal();

        push({ event: 'peer_review_completed' });

        setTimeout(() => {
            // Show thank you page
            window.location.reload();
        }, 500);
    } catch (e) {
        errorMessage.value = 'Failed to submit review. Please try again.';
    } finally {
        loading.value = false;
    }
});
</script>

<style lang="less">
.review-modal {
    form {
        width: 100%;
        display: flex;
        flex-direction: column;
        text-align: left;
        margin-bottom: var(--spacing-xs);
    }

    .review-modal__question,
    .dn-form-field__label {
        margin-bottom: 1rem;
    }

    .review-modal__form-error {
        .typography-small();
        color: var(--color-highlight-error);
        margin-bottom: .5rem;
    }

}
</style>
